import React, { useState } from 'react';
import API from '../../services/api';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { Field, Row, Notice, inputStyle, selectStyle, apiErrors } from './formBits';

const BLANK = {
  payerId: '', priority: 'primary', memberId: '', groupNumber: '', planName: '', effectiveDate: '', terminationDate: '',
  relationship: 'self', subscriberFirstName: '', subscriberLastName: '', subscriberDob: '', subscriberSex: '',
  subscriberAddress: '', subscriberCity: '', subscriberState: '', subscriberZip: '',
  copayAmount: '', coinsurancePercent: '', deductibleNotes: '', authorizationSignedOn: '', isActive: true,
};
function fromPolicy(p) {
  if (!p) return BLANK;
  const s = (v) => (v === null || v === undefined ? '' : String(v));
  return {
    payerId: p.payerId, priority: p.priority, memberId: s(p.memberId), groupNumber: s(p.groupNumber), planName: s(p.planName),
    effectiveDate: s(p.effectiveDate), terminationDate: s(p.terminationDate), relationship: p.relationship || 'self',
    subscriberFirstName: s(p.subscriberFirstName), subscriberLastName: s(p.subscriberLastName), subscriberDob: s(p.subscriberDob), subscriberSex: s(p.subscriberSex),
    subscriberAddress: s(p.subscriberAddress), subscriberCity: s(p.subscriberCity), subscriberState: s(p.subscriberState), subscriberZip: s(p.subscriberZip),
    copayAmount: s(p.copayAmount), coinsurancePercent: s(p.coinsurancePercent), deductibleNotes: s(p.deductibleNotes),
    authorizationSignedOn: s(p.authorizationSignedOn), isActive: p.isActive !== false,
  };
}

export default function PolicyForm({ clientId, payers, initial, onDone, onCancel }) {
  const [form, setForm] = useState(fromPolicy(initial));
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const notSelf = form.relationship !== 'self';

  async function save(e) {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      if (initial && initial.id) await API.put(`/billing/policies/${initial.id}`, form);
      else await API.post(`/billing/clients/${clientId}/policies`, form);
      onDone();
    } catch (err) {
      const { fieldErrors, message } = apiErrors(err);
      setErrors(fieldErrors); setMsg({ tone: 'bad', text: message });
    } finally { setSaving(false); }
  }

  return (
    <Card style={{ border: '1px solid var(--border-color)', marginBottom: 12 }}>
      <form onSubmit={save}>
        <div style={{ fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4 }}>{initial && initial.id ? 'Edit insurance policy' : 'Add insurance policy'}</div>
        <Row>
          <Field label="Payer" error={errors.payerId}>
            <select style={selectStyle} value={form.payerId} onChange={set('payerId')} aria-label="Payer">
              <option value="">Choose…</option>
              {payers.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          </Field>
          <Field label="Priority" error={errors.priority} style={{ maxWidth: 160 }}>
            <select style={selectStyle} value={form.priority} onChange={set('priority')} aria-label="Priority"><option value="primary">Primary</option><option value="secondary">Secondary</option></select>
          </Field>
          <Field label="Member ID" error={errors.memberId} hint="As printed on the card"><input style={inputStyle} value={form.memberId} onChange={set('memberId')} aria-label="Member ID" /></Field>
        </Row>
        <Row>
          <Field label="Group number" error={errors.groupNumber}><input style={inputStyle} value={form.groupNumber} onChange={set('groupNumber')} aria-label="Group number" /></Field>
          <Field label="Plan name" error={errors.planName}><input style={inputStyle} value={form.planName} onChange={set('planName')} aria-label="Plan name" /></Field>
          <Field label="Effective date" error={errors.effectiveDate} style={{ maxWidth: 170 }}><input style={inputStyle} type="date" value={form.effectiveDate} onChange={set('effectiveDate')} aria-label="Effective date" /></Field>
          <Field label="Termination date" error={errors.terminationDate} style={{ maxWidth: 170 }}><input style={inputStyle} type="date" value={form.terminationDate} onChange={set('terminationDate')} aria-label="Termination date" /></Field>
        </Row>
        <Row>
          <Field label="Client's relationship to subscriber" error={errors.relationship} style={{ maxWidth: 240 }}>
            <select style={selectStyle} value={form.relationship} onChange={set('relationship')} aria-label="Relationship">
              <option value="self">Self (client is the subscriber)</option><option value="spouse">Spouse</option><option value="child">Child</option><option value="other">Other</option>
            </select>
          </Field>
        </Row>
        {notSelf && (
          <>
            <Row>
              <Field label="Subscriber first name" error={errors.subscriberFirstName}><input style={inputStyle} value={form.subscriberFirstName} onChange={set('subscriberFirstName')} aria-label="Subscriber first name" /></Field>
              <Field label="Subscriber last name" error={errors.subscriberLastName}><input style={inputStyle} value={form.subscriberLastName} onChange={set('subscriberLastName')} aria-label="Subscriber last name" /></Field>
              <Field label="Subscriber date of birth" error={errors.subscriberDob} style={{ maxWidth: 170 }}><input style={inputStyle} type="date" value={form.subscriberDob} onChange={set('subscriberDob')} aria-label="Subscriber date of birth" /></Field>
              <Field label="Sex" error={errors.subscriberSex} style={{ maxWidth: 110 }}>
                <select style={selectStyle} value={form.subscriberSex} onChange={set('subscriberSex')} aria-label="Subscriber sex"><option value="">—</option><option value="F">F</option><option value="M">M</option><option value="U">U</option></select>
              </Field>
            </Row>
            <Row>
              <Field label="Subscriber address" error={errors.subscriberAddress}><input style={inputStyle} value={form.subscriberAddress} onChange={set('subscriberAddress')} aria-label="Subscriber address" /></Field>
              <Field label="City" error={errors.subscriberCity} style={{ maxWidth: 180 }}><input style={inputStyle} value={form.subscriberCity} onChange={set('subscriberCity')} aria-label="Subscriber city" /></Field>
              <Field label="State" error={errors.subscriberState} style={{ maxWidth: 90 }}><input style={inputStyle} value={form.subscriberState} onChange={set('subscriberState')} maxLength={2} aria-label="Subscriber state" /></Field>
              <Field label="ZIP" error={errors.subscriberZip} style={{ maxWidth: 120 }}><input style={inputStyle} value={form.subscriberZip} onChange={set('subscriberZip')} aria-label="Subscriber ZIP" /></Field>
            </Row>
          </>
        )}
        <Row>
          <Field label="Copay ($)" error={errors.copayAmount} style={{ maxWidth: 130 }}><input style={inputStyle} value={form.copayAmount} onChange={set('copayAmount')} aria-label="Copay" /></Field>
          <Field label="Coinsurance (%)" error={errors.coinsurancePercent} style={{ maxWidth: 140 }}><input style={inputStyle} value={form.coinsurancePercent} onChange={set('coinsurancePercent')} aria-label="Coinsurance percent" /></Field>
          <Field label="Deductible notes" error={errors.deductibleNotes}><input style={inputStyle} value={form.deductibleNotes} onChange={set('deductibleNotes')} aria-label="Deductible notes" /></Field>
          <Field label="Insurance authorization signed on" error={errors.authorizationSignedOn} hint="Assignment of benefits + release; claims need this" style={{ maxWidth: 220 }}><input style={inputStyle} type="date" value={form.authorizationSignedOn} onChange={set('authorizationSignedOn')} aria-label="Authorization signed on" /></Field>
        </Row>
        <label style={{ display: 'inline-block', marginTop: 10, fontSize: 13 }}><input type="checkbox" checked={form.isActive} onChange={set('isActive')} /> Active policy</label>
        {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save policy'}</Button>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}
